// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { MoreHorizontal, Pencil, Power, PowerOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { cn } from "@/lib/utils";

interface TaskActionsMenuProps {
  isActive: boolean;
  onToggleActive: () => void;
  /** Adds Edit at the top of the menu. The list rows pass it; the detail header has its own Edit button. */
  onEdit?: () => void;
  triggerClassName?: string;
}

export function TaskActionsMenu({ isActive, onToggleActive, onEdit, triggerClassName }: TaskActionsMenuProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  if (!hasPermission(PAGE_PERMISSIONS.tasksEdit)) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className={cn("h-8 w-8", triggerClassName)}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
        {onEdit && (
          <>
            <DropdownMenuItem onSelect={onEdit}>
              <Pencil className="h-4 w-4" />
              {t("buttons.edit")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          onSelect={onToggleActive}
          className={isActive ? "text-destructive focus:text-destructive" : ""}
        >
          {isActive ? (
            <>
              <PowerOff className="h-4 w-4 text-destructive" />
              {t("tasks.list.actions.deactivate")}
            </>
          ) : (
            <>
              <Power className="h-4 w-4" />
              {t("tasks.list.actions.activate")}
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
